import { queryCommonFlags } from "@/api/constants";
import { mergeLeadsV2 } from "@/modules/lead-page/api/lead-page.v2.api";
import {
  Group,
  MergeCustomerDtoRequest,
} from "@/modules/lead-page/dtos/index-v2";
import { getLeadMapping } from "@/modules/lead-page/mappings";
import { GetLeadResponse } from "@/modules/leads/dtos";
import { queryClient } from "@/queryClient";
import { UUID } from "crypto";
import React, { useMemo } from "react";
import { useEffect, useState, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Referral } from "@/modules/leads/types";
import { useDebounce } from "use-debounce";
import { getLeadSales } from "@/modules/leads/api/index.v2";
import { getTracks } from "@/modules/property-tracks/api";
import { JourneyRelationModes } from "@/modules/lead-page/types";

interface Selection {
  methodOfContact: string;
  location: string;
  referral?: string;
}

const useMergeLead = ({
  currentParentLead,
  setDisabledConfirmButton,
  submitMergeLead,
  setSubmitMergeLead,
}: {
  currentParentLead: GetLeadResponse | undefined;
  setDisabledConfirmButton: (boolean: boolean) => void;
  setSubmitMergeLead: (boolean: boolean) => void;
  setOpenConfirmMergeModal: (boolean: boolean) => void;
  submitMergeLead: boolean;
  openConfirmMergeModal: boolean;
}) => {
  const [searchParentLeadId, setSearchParentLeadId] = useState<
    string | number | UUID
  >(currentParentLead?.data?.nanoId ?? "");
  const [searchChildLeadId, setSearchChildLeadId] = useState<string | number>(
    "",
  );
  const [debouncedChildLeadId] = useDebounce(searchChildLeadId, 500);

  const [isFeedBackModalOpen, setIsFeedBackModalOpen] = useState(false);
  const [feedBackMsg, setFeedBackMsg] = useState(
    "Please search for a valid lead ID",
  );
  const [feedBackTitle, setFeedBackTitle] = useState("Lead not found");

  const [mergeObject, setMergeObject] =
    useState<MergeCustomerDtoRequest | null>(null);
  const [isSameLeadIdError, setIsSameLeadIdError] = useState(false);
  const [selectedName, setSelectedName] = useState(
    currentParentLead?.data?.lead?.name || "",
  );
  const [selectedReferral, setSelectedReferral] = useState("");

  const [selections, setSelections] = useState<Record<string, Selection>>({});
  const [isAllSelectionsComplete, setIsAllSelectionsComplete] = useState(false);

  const handleParentLeadChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSearchParentLeadId(event.target.value || "");
  };

  const handleChildLeadChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSearchChildLeadId(event.target.value || "");
  };

  const handleLeadSearch = (leadType: "parent" | "child") => {
    if (searchParentLeadId === searchChildLeadId) {
      setIsFeedBackModalOpen(true);
      setIsSameLeadIdError(true);
      setFeedBackTitle("Something went wrong");
      setFeedBackMsg("Parent and child lead cannot be the same");
      return;
    }

    if (leadType === "parent") {
      refetchParent();
    } else if (leadType === "child") {
      refetchChild();
    }
  };

  const handleSelectionChange = (
    leadTypeUid: string,
    field: keyof Selection,
    value: string,
  ) => {
    setSelections((prev) => ({
      ...prev,
      [leadTypeUid]: {
        ...prev[leadTypeUid],
        [field]: value,
      },
    }));
  };

  const {
    data: parentLeadData,
    refetch: refetchParent,
    isLoading: isParentLoading,
    isError: isParentError,
  } = useQuery({
    queryKey: ["getParentLead"],
    queryFn: ({ signal }) =>
      getLeadMapping({
        id: (searchParentLeadId as UUID) || undefined,
        signal,
        mode: JourneyRelationModes.MERGE_CUSTOMER,
      }),
    retry: false,
    ...queryCommonFlags,
    onError: () => {
      setIsFeedBackModalOpen(true);
      setDisabledConfirmButton(true);
    },
  });

  const {
    refetch: refetchChild,
    data: childLeadData,
    isFetching: isChildFetching,
    isError: isChildError,
  } = useQuery({
    queryKey: ["getChildLead", debouncedChildLeadId],
    queryFn: ({ signal }) =>
      getLeadMapping({
        id: debouncedChildLeadId as UUID,
        signal,
        mode: JourneyRelationModes.MERGE_CUSTOMER,
      }),
    enabled: Boolean(debouncedChildLeadId),
    retry: false,
    ...queryCommonFlags,
    onError: () => {
      setIsFeedBackModalOpen(true);
      setDisabledConfirmButton(true);
      setFeedBackMsg("Please search for a valid lead ID");
      setFeedBackTitle("Lead not found");
    },
  });

  // Get all parent customer sales (for all leads under parent customer)
  const { data: allParentSales } = useQuery({
    queryKey: ["getAllParentSales", parentLeadData?.data?.lead?.customer_uid],
    queryFn: async () => {
      if (!parentLeadData?.data?.lead?.customer?.leads) return { data: [] };

      const salesPromises = parentLeadData.data.lead.customer.leads.map(
        (lead) => getLeadSales(lead.uid),
      );

      const salesResults = await Promise.all(salesPromises);
      // Flatten all sales data into a single array
      const allSales = salesResults.flatMap((result) => result?.data || []);

      return { data: allSales };
    },
    enabled: Boolean(parentLeadData?.data?.lead?.customer?.leads?.length),
    retry: true,
    ...queryCommonFlags,
  });

  // Get all child customer sales (for all leads under child customer)
  const { data: allChildSales } = useQuery({
    queryKey: ["getAllChildSales", childLeadData?.data?.lead?.customer_uid],
    queryFn: async () => {
      if (!childLeadData?.data?.lead?.customer?.leads) return { data: [] };

      const salesPromises = childLeadData.data.lead.customer.leads.map((lead) =>
        getLeadSales(lead.uid),
      );

      const salesResults = await Promise.all(salesPromises);
      // Flatten all sales data into a single array
      const allSales = salesResults.flatMap((result) => result?.data || []);

      return { data: allSales };
    },
    enabled: Boolean(childLeadData?.data?.lead?.customer?.leads?.length),
    retry: true,
    ...queryCommonFlags,
  });

  const { data: childTrackCounts } = useQuery({
    queryKey: ["getChildTrackCounts", childLeadData?.data?.lead?.customer_uid],
    queryFn: async () => {
      if (!childLeadData?.data?.lead?.customer?.leads) return {};

      const counts: Record<string, number> = {};
      const trackPromises = childLeadData.data.lead.customer.leads.map(
        async (lead) => {
          const journeyUId = lead.journeys?.[0]?.uid;
          if (!journeyUId) return;

          const result = await getTracks({
            filters: { journeyUId },
            pagination: { page: 1, pageSize: 1 },
          });
          counts[journeyUId] = result?.data?.total ?? 0;
        },
      );

      await Promise.all(trackPromises);
      return counts;
    },
    enabled: Boolean(childLeadData?.data?.lead?.customer?.leads?.length),
    retry: true,
    ...queryCommonFlags,
  });

  const childPropertyTracksInfo = useMemo(() => {
    if (!childTrackCounts) return { hasAnyTracks: false, totalTracks: 0 };
    const totalTracks = Object.values(childTrackCounts).reduce(
      (sum, count) => sum + count,
      0,
    );
    return { hasAnyTracks: totalTracks > 0, totalTracks };
  }, [childTrackCounts]);

  const {
    mutate: mergeLeadMutation,
    isLoading: isMergeLeadLoading,
    isError: isMergeLeadError,
  } = useMutation({
    mutationFn: ({
      mergeObject,
      parentLeadId,
    }: {
      mergeObject: MergeCustomerDtoRequest;
      parentLeadId: string;
    }) => mergeLeadsV2(mergeObject, parentLeadId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getLead"] });
      queryClient.invalidateQueries({ queryKey: ["getLeadTracks"] });
      setDisabledConfirmButton(false);
      setIsFeedBackModalOpen(true);
      setFeedBackTitle("Lead Merged");
      setFeedBackMsg("Lead has been successfully merged");
    },
    onError: (error: any) => {
      setIsFeedBackModalOpen(true);
      setDisabledConfirmButton(true);
      setFeedBackTitle("Something went wrong");

      // Extract backend error message if available
      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message;

      setFeedBackMsg(backendMessage || "Please try again later");
    },
  });

  const leadTypeGroups = useMemo<Group[]>(() => {
    if (
      !parentLeadData?.data?.lead?.customer?.leads ||
      !childLeadData?.data?.lead?.customer?.leads
    ) {
      return [];
    }

    const groups = new Map();

    parentLeadData.data.lead.customer.leads.forEach((parentLead) => {
      if (parentLead.leadType?.uid) {
        groups.set(parentLead.leadType.uid, {
          type: parentLead.leadType,
          parent: { ...parentLead, user: parentLeadData.data.lead.user },
          child: null,
        });
      }
    });

    childLeadData.data.lead.customer.leads.forEach((childLead) => {
      if (childLead.leadType?.uid) {
        if (groups.has(childLead.leadType.uid)) {
          groups.get(childLead.leadType.uid).child = {
            ...childLead,
            user: childLeadData.data.lead.user,
          };
        } else {
          groups.set(childLead.leadType.uid, {
            type: childLead.leadType,
            parent: null,
            child: { ...childLead, user: childLeadData.data.lead.user },
          });
        }
      }
    });

    return Array.from(groups.values());
  }, [parentLeadData, childLeadData]);

  const sortedLeadTypeGroups = useMemo(() => {
    const matching = leadTypeGroups
      .filter((g) => g.parent && g.child)
      .sort((a, b) =>
        a.type?.name?.toLowerCase().localeCompare(b.type?.name?.toLowerCase()),
      );

    const rest = leadTypeGroups
      .filter((g) => !g.parent || !g.child)
      .sort((a, b) =>
        a.type?.name?.toLowerCase().localeCompare(b.type?.name?.toLowerCase()),
      );

    return [...matching, ...rest];
  }, [leadTypeGroups]);

  const isValidListingMerge = useMemo(() => {
    const childListingsOnNonAcceptingTypes = sortedLeadTypeGroups
      .filter((group) => !group.type.acceptListing)
      .flatMap((group) => group.child?.listings ?? []);

    if (childListingsOnNonAcceptingTypes.length === 0) return true;

    const hasAcceptingParent = sortedLeadTypeGroups.some(
      (group) => group.parent?.leadType.acceptListing === true,
    );

    return hasAcceptingParent;
  }, [sortedLeadTypeGroups]);

  const handleMergeLeads = useCallback(() => {
    if (mergeObject && parentLeadData?.data.lead.customer_uid) {
      mergeLeadMutation({
        mergeObject,
        parentLeadId: parentLeadData.data.lead.customer_uid,
      });
    }
  }, [mergeObject, parentLeadData, mergeLeadMutation]);

  useEffect(() => {
    if (submitMergeLead) {
      handleMergeLeads();
      setSubmitMergeLead(false);
    }
  }, [submitMergeLead, handleMergeLeads, setSubmitMergeLead]);

  useEffect(() => {
    if (
      !childLeadData?.data?.lead?.customer?.leads ||
      !parentLeadData?.data?.lead?.customer?.leads
    ) {
      setDisabledConfirmButton(true);
      setIsAllSelectionsComplete(false);
      return;
    }

    if (
      parentLeadData.data.lead.customer_uid ===
      childLeadData.data.lead.customer_uid
    ) {
      setDisabledConfirmButton(true);
      setIsAllSelectionsComplete(false);
      setIsFeedBackModalOpen(true);
      setFeedBackTitle("Cannot Merge Leads");
      setFeedBackMsg(
        "Leads belong to the same customer number, please archive the duplicate or unwanted lead",
      );
      return;
    }

    const parentLeads = parentLeadData.data.lead.customer.leads;
    const childLeads = childLeadData.data.lead.customer.leads;

    const parentLeadTypeUIds = new Set(parentLeads.map((l) => l.leadType?.uid));
    const parentCategoryIds = new Set(
      parentLeads
        .map((l) => l.leadType?.leadTypeCategoryId)
        .filter((id): id is number => id != null),
    );

    const collidingChild = childLeads.find(
      (child) =>
        child.leadType?.leadTypeCategoryId != null &&
        !parentLeadTypeUIds.has(child.leadType.uid) &&
        parentCategoryIds.has(child.leadType.leadTypeCategoryId),
    );

    if (collidingChild) {
      const collidingParent = parentLeads.find(
        (p) =>
          p.leadType?.leadTypeCategoryId ===
          collidingChild.leadType?.leadTypeCategoryId,
      );
      const parentLeadTypeName = collidingParent?.leadType?.name ?? "";
      const childLeadTypeName = collidingChild.leadType?.name ?? "";

      setDisabledConfirmButton(true);
      setIsAllSelectionsComplete(false);
      setIsFeedBackModalOpen(true);
      setFeedBackTitle("Cannot Merge Leads");
      setFeedBackMsg(
        `The selected customers have leads that belong to lead types within the same category (${parentLeadTypeName}, ${childLeadTypeName}). Merging is not supported for this scenario at the moment. Please contact the support team.`,
      );
      return;
    }

    if (!isValidListingMerge) {
      setDisabledConfirmButton(true);
      setIsAllSelectionsComplete(false);
      setIsFeedBackModalOpen(true);
      setFeedBackTitle("Cannot Merge Leads");
      setFeedBackMsg(
        "One or more child leads have listings, but the parent has no lead with type that can accept listings",
      );
      return;
    }

    const mergeLeadsDto: any[] = [];
    let hasMatchingLeadTypes = false;
    let allSelectionsComplete = true;

    parentLeads.forEach((parentLead) => {
      const childLead = childLeads.find(
        (child) => child.leadType?.uid === parentLead.leadType?.uid,
      );
      if (childLead && parentLead.leadType?.uid) {
        hasMatchingLeadTypes = true;
        const selection = selections[parentLead.leadType.uid] || {};

        if (!selection.methodOfContact || !selection.location) {
          allSelectionsComplete = false;
        }

        const activeReferral = Array.isArray(parentLead.referral)
          ? parentLead.referral.find((r: Referral) => r.isExpired === false)
          : null;

        mergeLeadsDto.push({
          leadTypeUId: parentLead.leadType.uid,
          methodOfContact:
            selection.methodOfContact === parentLead.methodOfContact?.uid
              ? "PRIMARY"
              : "SECONDARY",
          location: !parentLead.location?.name
            ? selection.location === "not set" || selection.location === "none"
              ? "PRIMARY"
              : "SECONDARY"
            : selection.location === parentLead.location.name
              ? "PRIMARY"
              : "SECONDARY",
          ...(selection.referral && {
            referral: !activeReferral
              ? selection.referral === "not set" ||
                selection.referral === "none"
                ? "PRIMARY"
                : "SECONDARY"
              : selection.referral === activeReferral.referrer?.uid
                ? "PRIMARY"
                : "SECONDARY",
          }),
          mergeJourneysDto: [
            {
              primaryJourneyUId: parentLead?.journeys?.[0]?.uid,
              secondaryJourneyUId: childLead?.journeys?.[0]?.uid,
            },
          ],
        });
      }
    });

    setMergeObject({
      secondaryCustomerUId: childLeadData.data.lead.customer_uid as string,
      name:
        selectedName === parentLeadData.data.lead.name
          ? "PRIMARY"
          : "SECONDARY",
      mergeLeadsDto: hasMatchingLeadTypes ? mergeLeadsDto : [],
    });

    setIsAllSelectionsComplete(allSelectionsComplete);
  }, [
    selectedName,
    selections,
    childLeadData,
    parentLeadData,
    setDisabledConfirmButton,
    isValidListingMerge,
  ]);

  useEffect(() => {
    if (
      ((parentLeadData && childLeadData) ||
        (currentParentLead && childLeadData)) &&
      isAllSelectionsComplete
    ) {
      setDisabledConfirmButton(false);
    } else {
      setDisabledConfirmButton(true);
    }
  }, [
    parentLeadData,
    childLeadData,
    currentParentLead,
    setDisabledConfirmButton,
    isAllSelectionsComplete,
  ]);

  return {
    handleParentLeadChange,
    handleChildLeadChange,
    handleSelectionChange,
    handleMergeLeads,
    handleLeadSearch,
    setSelectedName,
    setSelectedReferral,
    setIsFeedBackModalOpen,
    feedBackMsg,
    feedBackTitle,
    isChildError,
    isParentError,
    isChildFetching,
    isParentLoading,
    isMergeLeadError,
    isSameLeadIdError,
    isMergeLeadLoading,
    isFeedBackModalOpen,
    isAllSelectionsComplete,
    selections,
    selectedName,
    childLeadData,
    parentLeadData,
    selectedReferral,
    searchChildLeadId,
    searchParentLeadId,
    sortedLeadTypeGroups,
    parentLeadSales: allParentSales,
    childLeadSales: allChildSales,
    childPropertyTracksInfo,
  };
};

export default useMergeLead;
